import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { DateModel } from '@app/core/components/date-range/date-range.component';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { EdiOrderHeadsGet, EdiOrderRow, EdiOrderStatus, EdiParams, EdiService } from '@app/services/edi.service';

@Component({
    selector: 'tt-edi-order-heads',
    templateUrl: './edi-order-heads.component.html',
    styleUrls: ['./edi-order-heads.component.css'],
    standalone: false,
})
export class EdiOrderHeadsComponent implements OnInit {
    public page?: EdiOrderHeadsGet;
    public statuses: EdiOrderStatus[] = [];
    public gridHead?: Grid<EdiOrderRow>;
    public gridLine?: Grid<EdiOrderRow>;
    public gridProperty?: Grid<EdiOrderRow>;
    public showFilters = true;
    public searching = false;
    private params: EdiParams = { argtype: '', argvalue: '' };

    constructor(private readonly edi: EdiService, @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        this.params = { argtype: String(this.navigator?.params['argtype'] ?? ''), argvalue: String(this.navigator?.params['argvalue'] ?? '') };
        this.showFilters = this.params.argtype !== 'edi_orderhead_keyno';
        await Promise.all([this.loadPage(), this.loadStatuses()]);
    }

    private async loadPage(): Promise<void> { this.page = await this.edi.getOrderHeads(this.params); await this.loadHeads(); }
    private async loadStatuses(): Promise<void> { this.statuses = await this.edi.getStatuses(); }

    private createGrid(name: string, method: number): Grid<EdiOrderRow> {
        const grid = new Grid<EdiOrderRow>().setRememberId('w_ediorderheads_' + name + '_grid')
            .setLoadDataFunction({ method, parameters: { ...this.page } })
            .setSpecialFunc({ newTab: true }).setCSSOptions({ height: 'fill' })
            .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false,
                selectable: 'single', filterable: false });
        if (grid.dataTask.removeRow) grid.dataTask.removeRow.confirm = false;
        return grid;
    }

    private async loadHeads(): Promise<void> {
        if (!this.page) return;
        this.page.selected_edi_orderhead_keyno = this.showFilters ? '0' : this.params.argvalue;
        if (!this.showFilters) this.page.status_id = '30';
        if (!this.gridHead) {
            this.gridHead = this.createGrid('ediorderhead', 3667);
            this.gridHead.onSelect = event => { if (event.$item) void this.selectHead(event.$item); };
        } else {
            this.gridHead.setLoadDataFunction({ method: 3667, parameters: { ...this.page } });
            await this.gridHead.gridfunc?.rebind();
        }
    }

    public async onHeadReady(ready: boolean): Promise<void> {
        if (!ready || !this.page) return;
        const first = this.gridHead?.gridfunc?.getRows(false, true, true)[0];
        if (first) {
            await this.selectHead(first, true);
            this.gridHead?.gridfunc?.selectRow(0);
        } else {
            this.page.selected_edi_orderhead_keyno = '0'; this.page.selected_edi_orderline_keyno = '0';
            this.gridLine = undefined; this.gridProperty = undefined;
        }
    }

    public async selectHead(row: EdiOrderRow, force = false): Promise<void> {
        if (!this.page || !row.edi_orderhead_keyno) return;
        if (!force && String(this.page.selected_edi_orderhead_keyno) === String(row.edi_orderhead_keyno) && this.gridLine) return;
        this.page.selected_edi_orderhead_keyno = row.edi_orderhead_keyno;
        this.page.selected_edi_orderline_keyno = '0';
        if (!this.gridLine) {
            this.gridLine = this.createGrid('ediorderline', 3671);
            this.gridLine.onSelect = event => { if (event.$item) void this.selectLine(event.$item); };
        } else {
            this.gridLine.setLoadDataFunction({ method: 3671, parameters: { ...this.page } });
            await this.gridLine.gridfunc?.read();
        }
        await this.loadProperties();
    }

    public async onLineReady(ready: boolean): Promise<void> {
        if (!ready || !this.page) return;
        const first = this.gridLine?.gridfunc?.getRows(false, true, true)[0];
        if (first) { await this.selectLine(first); this.gridLine?.gridfunc?.selectRow(0); }
        else { this.page.selected_edi_orderline_keyno = '0'; await this.loadProperties(); }
        this.onGridResized();
    }

    public async selectLine(row: EdiOrderRow): Promise<void> {
        if (!this.page || !row.edi_orderline_keyno || String(this.page.selected_edi_orderline_keyno) === String(row.edi_orderline_keyno)) return;
        this.page.selected_edi_orderline_keyno = row.edi_orderline_keyno;
        await this.loadProperties();
    }

    private async loadProperties(): Promise<void> {
        if (!this.page) return;
        if (!this.gridProperty) this.gridProperty = this.createGrid('ediorderlineproperty', 3688);
        else {
            this.gridProperty.setLoadDataFunction({ method: 3688, parameters: { ...this.page } });
            await this.gridProperty.gridfunc?.read();
        }
    }

    public async searchItems(): Promise<void> {
        if (!this.page || this.searching) return;
        this.searching = true;
        try { await this.loadHeads(); } finally { this.searching = false; }
    }

    public async onSearchChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.ordersearch) return;
        this.page.ordersearch = value;
        if (!value) await this.searchItems();
    }

    public async onSearchBlur(): Promise<void> { if (this.page?.ordersearch) await this.searchItems(); }

    public async onStatusChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.status_id) return;
        this.page.status_id = value;
        await this.searchItems();
    }

    public async onDateRangeChanged(range: DateModel): Promise<void> {
        if (!this.page) return;
        const intervals = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];
        const index = intervals.indexOf(range.dateselector_index);
        const value = index >= 0 ? String(index + 1) : range.dateselector_index;
        this.page.date_fom = range.date_fom; this.page.date_tom = range.date_tom;
        if (Number(value) > 0) this.page.dateselector_index = value;
        if (this.page.date_fom && this.page.date_tom && this.page.dateselector_index) {
            this.page.date_fomtom = this.page.date_fom + this.page.date_tom;
            await this.searchItems();
        }
    }

    public onGridResized(): void { this.gridHead?.gridfunc?.resize(); this.gridLine?.gridfunc?.resize(); this.gridProperty?.gridfunc?.resize(); }
}
